import { makeGqlQuery } from '@/utilities/gqlQuery';
import { gql } from '@/utilities/gql';
import type { ResolvedQueryData } from '@/utilities/genericTypes';
import {
  NARRATIVE_CARD_FRAGMENT,
  FAIR_VALUE_HISTORY_GRAPH_NARRATIVE_FRAGMENT,
} from '../fragments';
import { useMemo, useState } from 'react';

const QUERY = gql(
  `
 query NarrativeChart($canonicalUrl: String!) {
  company: Company(canonicalURL: $canonicalUrl) {
      myNarrative {
        ...NarrativeCardFields
        ...FairValueHistoryGraphNarrativeFields
      }
      myValuation {
        narrative {
          ...NarrativeCardFields
          ...FairValueHistoryGraphNarrativeFields
        }
      }
      # Disabled: Sponsored narratives not necessarily written for this company
      # sponsoredNarratives {
      #   ...NarrativeCardFields
      #   ...FairValueHistoryGraphNarrativeFields
      # }
    }
    narratives(first: 3, filter: { canonicalUrl: $canonicalUrl }, sort: { field: POPULARITY, direction: DESC }) {
      edges {
        node {
          commentKey
          commentCount
          company {
            canonicalCommunityUrl
          }
          ...NarrativeCardFields
          ...FairValueHistoryGraphNarrativeFields
        }
      }
    }
  }
`,
  [NARRATIVE_CARD_FRAGMENT, FAIR_VALUE_HISTORY_GRAPH_NARRATIVE_FRAGMENT]
);

const useNarrativeChartQuery = makeGqlQuery(QUERY, {
  staleTime: 3600000,
  tags: ['narrative', 'narrative-chart-list'],
});

export type Narrative = ResolvedQueryData<
  typeof useNarrativeChartQuery
>['narratives']['edges'][number]['node'];
const getUniqueById = <T extends { id: string }>(items: T[]): T[] => {
  // Add items in priority order, Map will handle deduplication by ID
  const itemMap = new Map<string, T>();
  for (const item of items) {
    if (!itemMap.has(item.id)) {
      itemMap.set(item.id, item);
    }
  }
  return Array.from(itemMap.values());
};

/**
 * Returns a list of narratives that threads together sponsored, myValuation, and myNarrative types
 */
export const useNarrativeChart = ({
  canonicalUrl,
}: {
  canonicalUrl: string;
}): {
  narratives: Narrative[];
  selectedIndex: number;
  setSelectedIndex: (index: number) => void;
  isLoading: boolean;
} => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const response = useNarrativeChartQuery({ canonicalUrl });

  const narratives = useMemo(() => {
    const items = [
      // current user's set valuation
      response.data?.company.myValuation?.narrative,
      // current user's narrative
      response.data?.company.myNarrative,
      // other users narratives
      response.data?.narratives.edges.map((n) => n.node),
      // Disabled: Sponsored narratives not necessarily written for this company
      // response.data?.company.sponsoredNarratives,
    ]
      .flat()
      .filter(
        // Remove synthetic narratives
        // eg: "Discounted Cash Flow" / "Analyst Price Target" cards
        (n) => n?.valuation && !['dcf', 'analysts'].includes(n.valuation.type)
      ) as Narrative[];

    return getUniqueById(items);
  }, [response.data]);

  return {
    narratives,
    selectedIndex,
    setSelectedIndex,
    isLoading: response.isLoading,
  };
};
